import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useRole } from "@/contexts/RoleContext";
import { useGrade } from "@/contexts/GradeContext";
import { listChapters, startSession, startSubtopic, revisitRecap } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { ArrowLeft, Unlock, CheckCircle2, AlertCircle, BookOpen, Lock, Lightbulb, GraduationCap } from "lucide-react";
import PoolDualButtons from "@/components/PoolDualButtons";

export default function SubjectOverview() {
  const { subject } = useParams();
  const { learner } = useRole();
  const { selectedGrade } = useGrade();
  const nav = useNavigate();
  const [chapters, setChapters] = useState([]);
  const [openChapter, setOpenChapter] = useState(undefined);
  const [starting, setStarting] = useState(false);

  const teacherName = sessionStorage.getItem("teacher_name") || "";

  useEffect(() => {
    if (!learner) return;
    listChapters(decodeURIComponent(subject), learner.learner_id, selectedGrade).then((data) => {
      setChapters(data);
      // Default-open the first chapter that has any in-progress / stopped / started sub-topic;
      // otherwise the very first chapter so the page never feels empty.
      const flagged = data.find(c => c.sub_topics.some(s => s.skill_state || s.has_stop || s.in_progress_session));
      setOpenChapter((flagged || data[0])?.chapter);
    }).catch(() => setChapters([]));
  }, [subject, learner, selectedGrade]);

  const beginSubtopic = async (st) => {
    if (!learner) return;
    setStarting(true);
    try {
      const sess = await startSession(learner.learner_id, selectedGrade);
      const initialAction = await startSubtopic(sess.session_id, st.subject, st.sub_topic);
      // Forward the initial action (typically a recap) via router state so the
      // SessionRunner shows it instead of immediately advancing past it.
      nav(`/session/${sess.session_id}`, { state: { initialAction } });
    } finally {
      setStarting(false);
    }
  };

  const resumeSubtopic = (st) => {
    const sid = st?.in_progress_session?.session_id;
    if (sid) nav(`/session/${sid}`, {
      state: {
        resumed: true,
        subject: decodeURIComponent(subject),
        sub_topic: st.sub_topic,
        current_layer: st.in_progress_session?.current_layer,
      },
    });
  };

  const revisitSubtopic = async (st) => {
    if (!learner) return;
    setStarting(true);
    try {
      const sess = await startSession(learner.learner_id, selectedGrade);
      const initialAction = await revisitRecap(sess.session_id, st.subject, st.sub_topic);
      nav(`/session/${sess.session_id}`, { state: { initialAction } });
    } finally {
      setStarting(false);
    }
  };

  const totalSubtopics = chapters.reduce((acc, c) => acc + c.sub_topic_count, 0);

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8" data-testid="subject-overview">
      <Link to="/home" className="inline-flex items-center gap-1 text-sky-700 font-semibold hover:underline mb-4 tap-44" data-testid="back-home">
        <ArrowLeft className="w-4 h-4" /> Back
      </Link>
      <h1 className="font-display text-4xl md:text-5xl font-bold text-slate-800 mb-2" data-testid="subject-name">
        {decodeURIComponent(subject)}
      </h1>
      <p className="text-slate-600 mb-8" data-testid="subject-summary">
        {chapters.length > 0
          ? `${chapters.length} chapter${chapters.length > 1 ? "s" : ""} • ${totalSubtopics} sub-topic${totalSubtopics > 1 ? "s" : ""} in learning order`
          : "Loading chapters…"}
      </p>
      {teacherName && (
        <div className="flex items-center gap-1.5 -mt-6 mb-8" data-testid="subject-teacher-name">
          <GraduationCap className="w-4 h-4 text-sky-500" />
          <span className="text-sm font-medium text-slate-600">{teacherName}</span>
        </div>
      )}

      {chapters.length === 0 && (
        <div className="text-center text-slate-400 py-10" data-testid="no-chapters">Loading chapters…</div>
      )}

      {chapters.length > 0 && (
        <Accordion
          type="single"
          collapsible
          value={openChapter}
          onValueChange={setOpenChapter}
          className="space-y-3"
          data-testid="chapters-accordion"
        >
          {chapters.map((ch) => {
            const completedCount = ch.sub_topics.filter(s => s.skill_state?.pool_a_status === "established").length;
            const stopCount = ch.sub_topics.filter(s => s.has_stop && !s.teacher_unlocked).length;
            const chapterTeacherLocked = ch.sub_topics.length > 0 && ch.sub_topics.every(s => s.class_topic_locked);

            return (
              <AccordionItem
                key={ch.chapter}
                value={ch.chapter}
                className={`border-0 rounded-3xl mit-shadow bg-white border ${chapterTeacherLocked ? "border-slate-200" : "border-sky-100"}`}
                data-testid={`chapter-item-${ch.chapter_display_number}`}
              >
                <AccordionTrigger
                  className="px-6 py-5 hover:no-underline tap-44"
                  data-testid={`chapter-trigger-${ch.chapter_display_number}`}
                >
                  <div className="flex items-center gap-4 flex-1 text-left">
                    <div className={`w-11 h-11 rounded-2xl flex items-center justify-center font-display font-bold flex-shrink-0 ${chapterTeacherLocked ? "bg-slate-100 text-slate-400" : "bg-sky-100 text-sky-700"}`}>
                      {chapterTeacherLocked ? <Lock className="w-4 h-4" /> : ch.chapter_display_number}
                    </div>
                    <div className="flex-1">
                      <div className="text-xs uppercase font-semibold text-slate-400 tracking-wider">Chapter {ch.chapter_display_number}</div>
                      <h2 className={`font-display text-xl md:text-2xl font-bold leading-tight ${chapterTeacherLocked ? "text-slate-500" : "text-slate-800"}`} data-testid={`chapter-name-${ch.chapter_display_number}`}>
                        {ch.chapter}
                      </h2>
                      <div className="flex flex-wrap items-center gap-2 mt-1.5 text-xs">
                        <span className="text-slate-500">{ch.sub_topic_count} sub-topic{ch.sub_topic_count > 1 ? "s" : ""}</span>
                        {chapterTeacherLocked && (
                          <span className="px-2 py-0.5 rounded-full bg-slate-200 text-slate-600 font-semibold flex items-center gap-1" data-testid={`chapter-teacher-locked-${ch.chapter_display_number}`}>
                            <Lock className="w-3 h-3" /> Ask your teacher to unlock
                          </span>
                        )}
                        {!chapterTeacherLocked && completedCount > 0 && (
                          <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-semibold flex items-center gap-1">
                            <CheckCircle2 className="w-3 h-3" /> {completedCount} done
                          </span>
                        )}
                        {!chapterTeacherLocked && stopCount > 0 && (
                          <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-semibold flex items-center gap-1">
                            <AlertCircle className="w-3 h-3" /> {stopCount} revisit
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </AccordionTrigger>

                <AccordionContent className="px-6 pb-5">
                  <div className="space-y-3 pt-1" data-testid={`chapter-subtopics-${ch.chapter_display_number}`}>
                    {ch.sub_topics.map((st) => {
                      const state = st.skill_state;
                      const completed = state?.pool_a_status === "established";
                      const hasStop = st.has_stop && !st.teacher_unlocked;
                      const inProgress = !!st.in_progress_session;
                      const lockState = st.lock_state || "unlocked";
                      const teacherTopicLocked = !!st.class_topic_locked;
                      const hardLocked = !teacherTopicLocked && lockState === "hard_locked";
                      const softLocked = !teacherTopicLocked && lockState === "soft_locked";
                      const teacherUnlocked = !teacherTopicLocked && lockState === "teacher_unlocked";
                      const poolBDone = st.pool_b?.status === "established";
                      return (
                        <Card
                          key={st.sub_topic}
                          className={`rounded-2xl p-4 border flex flex-col sm:flex-row sm:items-center gap-3
                            ${teacherTopicLocked ? "border-slate-200 bg-slate-50 opacity-80"
                              : poolBDone        ? "border-emerald-200 bg-emerald-50/60"
                              : teacherUnlocked ? "border-2 border-teal-300 bg-teal-50/40"
                              : hardLocked      ? "border-slate-200 bg-slate-50/60 opacity-90"
                              : softLocked      ? "border-amber-100 bg-amber-50/30"
                              : "border-sky-100 bg-sky-50/40"}`}
                          data-testid={`subtopic-row-${st.sub_topic}`}
                          data-lock-state={teacherTopicLocked ? "class_topic_locked" : lockState}
                        >
                          <div className="flex items-start gap-3 flex-1">
                            <div className={`w-8 h-8 rounded-xl flex items-center justify-center text-sm font-bold flex-shrink-0
                              ${(hardLocked || teacherTopicLocked) ? "bg-white text-slate-400" : "bg-white text-sky-700"}`}>
                              {(hardLocked || teacherTopicLocked) ? <Lock className="w-4 h-4" /> : st.sub_topic_order}
                            </div>
                            <div className="flex-1">
                              <div className="flex flex-wrap items-center gap-2">
                                <h3 className={`font-display text-base md:text-lg font-bold ${(hardLocked || teacherTopicLocked) ? "text-slate-500" : "text-slate-800"}`}>
                                  {st.sub_topic}
                                </h3>
                                {st.is_foundational && (
                                  <span className="px-2 py-0.5 rounded-full bg-sky-100 text-sky-700 text-[10px] font-semibold uppercase tracking-wider" data-testid="foundational-tag">
                                    Foundational
                                  </span>
                                )}
                                {inProgress && !completed && !hardLocked && !teacherTopicLocked && (
                                  <span className="px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200 text-[10px] font-bold uppercase tracking-wider" data-testid="in-progress-tag">
                                    In progress
                                  </span>
                                )}
                                {completed && !poolBDone && !teacherTopicLocked && (
                                  <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-xs font-semibold flex items-center gap-1" data-testid="completed-tag">
                                    <CheckCircle2 className="w-3.5 h-3.5" /> Practice done
                                  </span>
                                )}
                                {poolBDone && !teacherTopicLocked && (
                                  <span className="px-2 py-0.5 rounded-full bg-emerald-200 text-emerald-800 text-xs font-semibold flex items-center gap-1" data-testid="complete-all-tag">
                                    <CheckCircle2 className="w-3.5 h-3.5" /> All done
                                  </span>
                                )}
                                {hasStop && !teacherTopicLocked && (
                                  <button
                                    onClick={(e) => { e.stopPropagation(); revisitSubtopic(st); }}
                                    disabled={starting}
                                    className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-xs font-semibold flex items-center gap-1 hover:bg-amber-200 active:scale-95 transition-all cursor-pointer disabled:opacity-60"
                                    data-testid="revisit-btn"
                                  >
                                    <AlertCircle className="w-3.5 h-3.5" /> Revisit
                                  </button>
                                )}
                                {teacherUnlocked && (
                                  <span className="px-2 py-0.5 rounded-full bg-teal-100 text-teal-800 text-xs font-semibold flex items-center gap-1" data-testid="teacher-unlocked-tag">
                                    <Unlock className="w-3.5 h-3.5" /> Unlocked by your teacher
                                  </span>
                                )}
                                {teacherTopicLocked && (
                                  <span className="px-2 py-0.5 rounded-full bg-slate-200 text-slate-600 text-xs font-semibold flex items-center gap-1" data-testid="class-topic-locked-tag">
                                    <Lock className="w-3.5 h-3.5" /> Locked by your teacher
                                  </span>
                                )}
                                {hardLocked && !teacherTopicLocked && (
                                  <span className="px-2 py-0.5 rounded-full bg-slate-200 text-slate-600 text-xs font-semibold flex items-center gap-1" data-testid="locked-tag">
                                    <Lock className="w-3.5 h-3.5" /> Locked
                                  </span>
                                )}
                              </div>
                              {teacherTopicLocked && (
                                <div className="text-sm text-slate-600 mt-1.5 flex items-start gap-1.5" data-testid="class-topic-locked-msg">
                                  <Lock className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                                  <span>Ask your teacher to unlock this topic</span>
                                </div>
                              )}
                              {hardLocked && !teacherTopicLocked && st.prerequisite_sub_topic && (
                                <div className="text-sm text-slate-600 mt-1.5 flex items-start gap-1.5" data-testid="hard-lock-msg">
                                  <BookOpen className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                                  <span>Complete <em className="not-italic font-semibold">{st.prerequisite_sub_topic}</em> first</span>
                                </div>
                              )}
                              {softLocked && !teacherTopicLocked && st.prerequisite_sub_topic && (
                                <div
                                  className="mt-2 rounded-xl border border-amber-200 bg-amber-50 text-amber-800 text-xs px-3 py-2 flex items-start gap-2"
                                  data-testid="soft-lock-tip"
                                >
                                  <Lightbulb className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                                  <span>Tip: finishing <em className="not-italic font-semibold">{st.prerequisite_sub_topic}</em> first will help here.</span>
                                </div>
                              )}
                            </div>
                          </div>

                          {(hardLocked || teacherTopicLocked) ? (
                            <div className="px-5 py-4 self-stretch sm:self-auto flex items-center text-slate-400 font-semibold" data-testid={`locked-subtopic-${st.sub_topic}`}>
                              <Lock className="w-4 h-4" />
                            </div>
                          ) : (
                            // Always render BOTH Practice + Challenge buttons.
                            // - hard_locked / class_topic_locked are handled above.
                            // - If the sub-topic has a paused session, tapping either
                            //   button resumes that session (preserves "buttons never
                            //   disappear on re-attempt").
                            // - Otherwise we start a fresh session.
                            <PoolDualButtons
                              subTopic={st.sub_topic}
                              pool_a={st.pool_a}
                              pool_b={st.pool_b}
                              lockState={lockState}
                              disabled={starting}
                              practiceLocked={hasStop}
                              onStart={() => (inProgress ? resumeSubtopic(st) : beginSubtopic(st))}
                            />
                          )}
                        </Card>
                      );
                    })}
                  </div>
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>
      )}
    </div>
  );
}
